import React from 'react';

/**
 * Why this row still has no document: Billingo already refused it.
 *
 * The queue used to show a clean row for a group that had been refused up to
 * nineteen times — the reason existed only in CloudWatch, which nobody at the
 * desk can read, so the only thing left to try was clicking again. This puts
 * Billingo's own message next to the Issue button.
 *
 * The message is shown RAW. It is written in the operator's language of work
 * (Billingo is the tool they then go and fix this in) and it names the advance
 * document id, which is the actionable half; any rewording of ours would lose
 * it. Rows refused before the reason was being stored show the count alone —
 * "refused 19 times" is itself the signal to stop clicking and go and look.
 */

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** "today", "yesterday", "4 days ago", else the date. */
export function whenRefused(iso, now = new Date()) {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const days = Math.floor((now.getTime() - then.getTime()) / ONE_DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return then.toISOString().slice(0, 10);
}

function BillingoFailureNote({ failure, now = undefined }) {
  if (!failure) return null;

  const attempts = Number(failure.attempts) || 1;
  const when = whenRefused(failure.at, now || new Date());
  const headline = [
    attempts > 1 ? `Billingo refused this ${attempts} times` : 'Billingo refused this',
    when,
  ].filter(Boolean).join(', ');

  const advances = (failure.advance_invoice_ids || []).filter(Boolean);

  return (
    <div className="mt-1" style={{ fontSize: '.75rem' }}>
      <div className="text-danger fw-medium">
        <i className="fa fa-triangle-exclamation me-1" />
        {headline}
        {failure.status ? ` (${failure.status})` : ''}
      </div>
      {failure.message ? (
        <div className="text-body-secondary" style={{ wordBreak: 'break-word' }}>
          {failure.message}
        </div>
      ) : (
        <div className="text-body-secondary fst-italic">
          No reason was recorded — check this group in Billingo before trying again.
        </div>
      )}
      {advances.length > 0 && (
        <div className="text-body-secondary">
          {`Advance${advances.length > 1 ? 's' : ''} Billingo named: ${advances.join(', ')}`}
        </div>
      )}
      {failure.created_by && (
        <div className="text-muted">{`last tried by ${failure.created_by}`}</div>
      )}
    </div>
  );
}

export default BillingoFailureNote;
